import { selectActiveOrgGroupSlug, useAppSelector } from "services"
import useAppRoutes from "../useAppRoutes/useAppRoutes"

interface IOrgGroupRoutesRoutes {
    currentOrgGroup: () => string
    orgGroup: (orgGroupId: string) => string
    orgGroupSlugWrapper: (targetUrl: string, fallback?: string) => string
}

/**
 * Hook that provides the current organization group context, direct access to an organization group with a supplied org group id, and a wrapper to fallback if there's no org group in state.
 *
 * @returns An object containing the following functions:
 * - `currentOrgGroup`: A function that returns the current organization group base route.
 * - `orgGroup`: A function that returns the URL for a specific organization group based on the supplied org group id.
 * - `orgGroupSlugWrapper`: A function that returns the URL for a target URL with the organization group slug appended, with an optional fallback URL if there's no org group in state.
 */
const useOrgGroupRoutes = (): IOrgGroupRoutesRoutes => {
    const currentOrgGroupSlug = useAppSelector(selectActiveOrgGroupSlug)

    const { rootRoute } = useAppRoutes()

    const currentOrgGroup = () =>
        currentOrgGroupSlug ? `${rootRoute()}/${currentOrgGroupSlug}` : rootRoute()

    // Will verify the value of currentOrgGroup is set to avoid undefined values
    // Defaults to application root
    // Not Yet Implemented - Using Placeholder to pass back URL
    const orgGroupSlugWrapper = (targetUrl: string, fallback?: string): string =>
        currentOrgGroupSlug && currentOrgGroupSlug.length > 0
            ? `${currentOrgGroup()}/${targetUrl}`
            : fallback ?? rootRoute()

    const orgGroup = (orgGroupSlug) => `${rootRoute()}/${orgGroupSlug}`

    return {
        currentOrgGroup,
        orgGroup,
        orgGroupSlugWrapper
    }
}

export default useOrgGroupRoutes
